import { useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { BaseEdge, EdgeLabelRenderer, useInternalNode, useReactFlow, type EdgeProps } from '@xyflow/react';
import { createUpdateEdgeRouteCommand } from '../commands';
import {
  TABLE_NODE_FALLBACK_HEIGHT,
  TABLE_NODE_WIDTH,
  computeEdgeVisual,
  type BoxSize,
  type EdgeEndpoint,
  type Point,
} from '../geometry';
import { useCanvasStore } from '../store';
import type { CanvasEdge } from '../types';

/** Kolmá vzdálenost bodu od úsečky (pro nalezení nejbližšího segmentu při vkládání waypointu). */
function distanceToSegment(point: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(point.x - a.x, point.y - a.y);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}

/** Index do pole waypointů, na který se má vložit nový bod vytvořený tažením za segment `points[i]-points[i+1]`. */
function nearestSegmentInsertIndex(points: Point[], click: Point): number {
  let bestIndex = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < points.length - 1; i += 1) {
    const distance = distanceToSegment(click, points[i], points[i + 1]);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }
  return bestIndex;
}

type DragState = { index: number; isNew: boolean; position: Point };

/** Stisk na hit-path, u kterého ještě nevíme, jestli půjde o klik (výběr) nebo tažení (nový bod). */
type PendingSegment = { index: number; startClient: Point };

/** Posun myši od `pointerdown`, po kterém teprve stisk na čáře přeroste v tažení nového bodu. Pod tímto prahem jde o obyčejný klik = výběr vazby. */
const NEW_WAYPOINT_DRAG_THRESHOLD_PX = 4;

/**
 * Vizuální vykreslení FK vazby (E6-US2, rozšířeno Epic 5 o ukotvení na
 * konkrétní řádek sloupce a odvozenou kardinalitu, Epic 7 o ruční trasu).
 * Nepoužívá RF `Handle` pozice pro vlastní geometrii čáry — ta pořád počítá
 * „floating edge" přístup přes `useInternalNode`, jen teď kotví na
 * `sourceRowOffset`/`targetRowOffset` (svislý střed řádku, viz
 * `geometry.ts#columnRowCenterOffset`) místo na svislý střed celé tabulky.
 *
 * E7-US4 — trasa je jednoduchá polyline skrz `data.waypoints` (žádný
 * ortogonální/bezier styl v MVP, epic-7 kap. 2). Stisk na viditelné čáře
 * (přes širší neviditelnou "hit" cestu) sám o sobě nic nezakládá — dokud se
 * kurzor nepohne o víc než `NEW_WAYPOINT_DRAG_THRESHOLD_PX`, jde jen o
 * `pendingSegment`; obyčejný klik (pointerup bez překročení prahu) proto
 * nevytvoří žádný bod a probublá jako normální klik na hranu, který RF sám
 * vybere (`edge.selected`, viz `CanvasView.tsx#handleSelectionChange`) a
 * zvýrazní (`.react-flow__edge.selected`, `styles.css`). Až překročení prahu
 * založí nový lomový bod přímo pod kurzorem a gesto pokračuje jako tažení
 * TOHOTO bodu; tažení existujícího bodu ho jen přesune (beze změny/bez
 * prahu — tam žádná nejednoznačnost s "kliknutím na čáru" není); dvojklik na
 * bod ho odebere. Commit (jedna `createUpdateEdgeRouteCommand`,
 * `isManuallyRouted: true`) proběhne až na `pointerup`/dvojkliku — živý
 * pohyb během tažení je jen lokální state, žádné volání `execute()` na
 * framu (stejný princip jako `onNodeDragStop` u tažení tabulky v
 * `CanvasView.tsx`).
 */
export function ForeignKeyEdge({ id, source, target, data }: EdgeProps<CanvasEdge>) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const { execute } = useCanvasStore();
  const { screenToFlowPosition } = useReactFlow();
  const [drag, setDrag] = useState<DragState | null>(null);
  const [pendingSegment, setPendingSegment] = useState<PendingSegment | null>(null);

  const waypoints = useMemo(() => {
    if (!drag) return data?.waypoints ?? [];
    const base = data?.waypoints ?? [];
    if (drag.isNew) {
      const next = [...base];
      next.splice(drag.index, 0, drag.position);
      return next;
    }
    return base.map((waypoint, index) => (index === drag.index ? drag.position : waypoint));
  }, [data?.waypoints, drag]);

  const commitWaypoints = (nextWaypoints: Point[]) => {
    if (!data) return;
    execute(
      createUpdateEdgeRouteCommand(
        data.foreignKey.id,
        { waypoints: data.waypoints, isManuallyRouted: data.isManuallyRouted },
        { waypoints: nextWaypoints, isManuallyRouted: true },
      ),
    );
  };

  // `button !== 0` chrání proti pravému tlačítku — bez tohohle right-click,
  // co otevírá kontextové menu (E7-US5 "Resetovat trasu"), NEZÁMĚRNĚ založil
  // i nový/přesunutý waypoint, protože `pointerdown`/`pointerup` events se
  // spouští pro JAKÉKOLIV tlačítko myši, ne jen levé.
  const beginDrag = (state: DragState) => (event: ReactPointerEvent) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    (event.target as Element).setPointerCapture(event.pointerId);
    setDrag(state);
  };

  // Zachytí pointer hned (jako `beginDrag`), ale nezaloží žádný bod — teprve
  // `handlePointerMove` po překročení prahu z `pendingSegment` udělá skutečný
  // `drag`. Obyčejný klik (pointerup dřív, než k tomu dojde) tak nechá vazbu
  // jen probublat k RF vlastnímu výběru hrany.
  const beginPendingSegment = (index: number) => (event: ReactPointerEvent) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    (event.target as Element).setPointerCapture(event.pointerId);
    setPendingSegment({ index, startClient: { x: event.clientX, y: event.clientY } });
  };

  const handlePointerMove = (event: ReactPointerEvent) => {
    if (drag) {
      setDrag({ ...drag, position: screenToFlowPosition({ x: event.clientX, y: event.clientY }) });
      return;
    }
    if (!pendingSegment) return;
    const distance = Math.hypot(
      event.clientX - pendingSegment.startClient.x,
      event.clientY - pendingSegment.startClient.y,
    );
    if (distance < NEW_WAYPOINT_DRAG_THRESHOLD_PX) return;
    setPendingSegment(null);
    setDrag({
      index: pendingSegment.index,
      isNew: true,
      position: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
    });
  };

  const handlePointerUp = (event: ReactPointerEvent) => {
    if (drag) {
      event.stopPropagation();
      const nextWaypoints = [...(data?.waypoints ?? [])];
      if (drag.isNew) nextWaypoints.splice(drag.index, 0, drag.position);
      else nextWaypoints[drag.index] = drag.position;
      commitWaypoints(nextWaypoints);
      setDrag(null);
      return;
    }
    // Práh nikdy nepadl — jde o klik, ne o tažení. Žádný bod se nezakládá.
    setPendingSegment(null);
  };

  const removeWaypoint = (index: number) => (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    commitWaypoints((data?.waypoints ?? []).filter((_, candidateIndex) => candidateIndex !== index));
  };

  if (!sourceNode || !targetNode || !data) return null;

  const sourceSize: BoxSize = {
    width: sourceNode.measured.width ?? TABLE_NODE_WIDTH,
    height: sourceNode.measured.height ?? TABLE_NODE_FALLBACK_HEIGHT,
  };
  const targetSize: BoxSize = {
    width: targetNode.measured.width ?? TABLE_NODE_WIDTH,
    height: targetNode.measured.height ?? TABLE_NODE_FALLBACK_HEIGHT,
  };

  const sourceEndpoint: EdgeEndpoint = {
    positionAbsolute: sourceNode.internals.positionAbsolute,
    size: sourceSize,
    rowOffset: data.sourceRowOffset,
  };
  const targetEndpoint: EdgeEndpoint = {
    positionAbsolute: targetNode.internals.positionAbsolute,
    size: targetSize,
    rowOffset: data.targetRowOffset,
  };
  const isSelfReference = source === target;
  // Sdíleno se statickým HTML exportem (Epic 12, `export/htmlExport.ts`) —
  // stejná funkce, stejný výsledek, aby export vypadal přesně jako canvas.
  const visual = computeEdgeVisual(sourceEndpoint, targetEndpoint, waypoints, isSelfReference);

  const cardinalityLabel = (text: string, point: Point, dx: number) =>
    data.showCardinalityLabel ? (
      <div
        className="fk-edge__cardinality"
        style={{ transform: `translate(-50%, -50%) translate(${point.x + dx}px, ${point.y}px)` }}
      >
        {text}
      </div>
    ) : null;

  const waypointMarkers = (points: Point[]) =>
    points.map((point, index) => (
      <div
        key={index}
        className="fk-edge__waypoint"
        style={{ transform: `translate(-50%, -50%) translate(${point.x}px, ${point.y}px)` }}
        onPointerDown={beginDrag({ index, isNew: false, position: point })}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={removeWaypoint(index)}
      />
    ));

  const hitPath = (path: string, points: Point[]) => (
    <path
      d={path}
      className="fk-edge__hit-path"
      fill="none"
      stroke="transparent"
      strokeWidth={16}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        const click = screenToFlowPosition({ x: event.clientX, y: event.clientY });
        beginPendingSegment(nearestSegmentInsertIndex(points, click))(event);
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    />
  );

  // Hit-path (pro `nearestSegmentInsertIndex`/`distanceToSegment`) používá
  // vždy rovné segmenty mezi ukotvujícími body a waypointy — i pro
  // self-referenční smyčku bez waypointů, kde se VIDITELNÁ čára kreslí jako
  // bezier (beze změny chování oproti stavu před touhle refaktorizací).
  const hitPoints = [visual.sourceLabelPoint, ...waypoints, visual.targetLabelPoint];

  return (
    <>
      <BaseEdge id={id} path={visual.path} className={data.isEndpointTableSelected ? 'fk-edge-path--table-selected' : undefined} />
      {hitPath(visual.path, hitPoints)}
      <EdgeLabelRenderer>
        {cardinalityLabel(data.sourceCardinality === 'one' ? '1' : 'N', visual.sourceLabelPoint, visual.sourceLabelDx)}
        {cardinalityLabel('1', visual.targetLabelPoint, visual.targetLabelDx)}
        {waypointMarkers(waypoints)}
      </EdgeLabelRenderer>
    </>
  );
}
